import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import { useAuth } from '../../contexts/AuthContext';
import { usePresentation } from '../../contexts/PresentationContext';
import AbaVisaoGeral from './AbaVisaoGeral';
import AbaMapa from './AbaMapa';
import AbaVencimentos from './AbaVencimentos';
import AbaCorretivas from './AbaCorretivas';
import AbaEscopo from './AbaEscopo';
import AbaChecklist from './AbaChecklist';
import RelatorioImpressao from './RelatorioImpressao';
import ChecklistImpressao from './ChecklistImpressao';
import * as hospitaisApi from '../../api/hospitais';

const ABAS_BASE = [
  { key: 'visao-geral', label: 'Visão Geral' },
  { key: 'mapa', label: 'Mapa de Conformidade' },
  { key: 'vencimentos', label: 'Vencimentos' },
  { key: 'corretivas', label: 'Corretivas' },
];

export default function HospitalDetalhe() {
  const { idHospital } = useParams();
  const { user } = useAuth();
  const { apresentacaoAtiva, hospitaisPermitidos } = usePresentation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hospital, setHospital] = useState(null);
  const [erro, setErro] = useState('');
  const [abaAtiva, setAbaAtiva] = useState(() => searchParams.get('aba') || 'visao-geral');
  const [requisitoAlvo, setRequisitoAlvo] = useState(() => searchParams.get('requisito'));
  const [selecionadosChecklist, setSelecionadosChecklist] = useState(new Set());
  const requisicaoAtual = useRef(0);

  const irParaRequisito = (idRequisito) => {
    setAbaAtiva('mapa');
    setRequisitoAlvo(idRequisito);
  };

  // Limpa a classe que faz o Checklist (em vez do Relatório completo) aparecer no @media
  // print — sem isso, um "Gerar Checklist" anterior faria o próximo "Exportar PDF" (de
  // qualquer aba) imprimir o checklist antigo em vez do relatório completo.
  useEffect(() => {
    const limpar = () => document.body.classList.remove('imprimir-checklist');
    window.addEventListener('afterprint', limpar);
    return () => window.removeEventListener('afterprint', limpar);
  }, []);

  // podeGerenciar: mexe no hospital em si (editar cadastro, Iniciar Trabalho, Escopo) — nunca
  // o auditor_hospital, mesmo lançando avaliação nesse mesmo hospital.
  const podeGerenciar = user.perfil === 'operador' || user.perfil === 'admin';
  // podeAvaliar: lança avaliação e gera Checklist — operador/admin (em qualquer hospital) e
  // auditor_hospital (só no hospital ao qual foi vinculado).
  const podeAvaliar = podeGerenciar || user.perfil === 'auditor_hospital';
  const foraDaApresentacao = apresentacaoAtiva && !hospitaisPermitidos.includes(idHospital);
  const foraDoEscopo =
    (['cliente', 'auditor_hospital'].includes(user.perfil) && user.id_hospital !== idHospital) || foraDaApresentacao;

  const carregar = async () => {
    const minhaRequisicao = ++requisicaoAtual.current;
    setErro('');
    try {
      const { data } = await hospitaisApi.obter(idHospital);
      if (requisicaoAtual.current !== minhaRequisicao) return; // resposta obsoleta (hospital já trocou)
      setHospital(data);
    } catch (err) {
      if (requisicaoAtual.current !== minhaRequisicao) return;
      setErro(extrairMensagemErro(err, 'Não foi possível carregar o hospital.'));
    }
  };

  useEffect(() => {
    if (foraDoEscopo) return;
    carregar();
    setSelecionadosChecklist(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idHospital]);

  if (foraDoEscopo) return <Navigate to="/dashboard" replace />;

  if (!hospital) return erro ? <ErroCarregamento mensagem={erro} onRetry={carregar} /> : <LoadingSpinner />;

  const abas = [
    ...ABAS_BASE,
    ...(podeGerenciar ? [{ key: 'escopo', label: 'Escopo' }] : []),
    ...(podeAvaliar ? [{ key: 'checklist', label: 'Gerar Checklist' }] : []),
  ];

  return (
    <div>
      <div className="tela-interativa">
        <PageHeader
          titulo={hospital.nome}
          subtitulo={hospital.cnpj || 'CNPJ não informado'}
          acoes={
            <>
              {podeGerenciar && (
                <button className="am-btn am-btn-outline" onClick={() => navigate('/hospitais')}>
                  <span className="ms">arrow_back</span>
                  Voltar
                </button>
              )}
              <button className="am-btn am-btn-dark" onClick={() => window.print()}>
                <span className="ms">picture_as_pdf</span>
                Exportar PDF
              </button>
            </>
          }
        />

        <div className="filter-tabs mb-3">
          {abas.map((a) => (
            <button
              key={a.key}
              type="button"
              className={`filter-tab${abaAtiva === a.key ? ' is-active' : ''}`}
              onClick={() => setAbaAtiva(a.key)}
            >
              {a.label}
            </button>
          ))}
        </div>

        {abaAtiva === 'visao-geral' && (
          <AbaVisaoGeral
            idHospital={idHospital}
            hospital={hospital}
            onHospitalAtualizado={setHospital}
            somenteLeitura={!podeGerenciar}
          />
        )}
        {abaAtiva === 'mapa' && (
          <AbaMapa
            idHospital={idHospital}
            somenteLeitura={!podeAvaliar}
            requisitoAlvo={requisitoAlvo}
            onRequisitoAlvoVisto={() => setRequisitoAlvo(null)}
          />
        )}
        {abaAtiva === 'vencimentos' && <AbaVencimentos idHospital={idHospital} onIrParaRequisito={irParaRequisito} />}
        {abaAtiva === 'corretivas' && <AbaCorretivas idHospital={idHospital} onIrParaRequisito={irParaRequisito} />}
        {abaAtiva === 'escopo' && podeGerenciar && <AbaEscopo idHospital={idHospital} />}
        {abaAtiva === 'checklist' && podeAvaliar && (
          <AbaChecklist
            idHospital={idHospital}
            selecionados={selecionadosChecklist}
            onSelecionadosChange={setSelecionadosChecklist}
          />
        )}
      </div>

      <RelatorioImpressao idHospital={idHospital} hospital={hospital} />
      <ChecklistImpressao idHospital={idHospital} hospital={hospital} selecionados={selecionadosChecklist} />
    </div>
  );
}
